import Banner from "@/components/pages/project-post/banner";
import { QUERY_ENDPOINTS } from "@/framework/utils/query-endpoints";
import { getUrqlClient } from "@/lib/urql-client";
import { sanitize } from "@/framework/utils/sanitize";
import Blueprints from "@/components/pages/project-post/blueprints";
import Testimonial from "@/components/pages/project-post/testimonial";
import AsSeenOn from "@/components/pages/project-post/as-seen-on";
import Priyanka from "@/components/pages/project-post/priyanka";
import NextPrev from "@/components/pages/project-post/next-prev";
import Gallery from "@/components/pages/project-post/gallery";
import Seo from "@/utils/seo";
import Slider1 from "@/components/pages/project-post/slider1";
import Grid from "@/components/pages/project-post/grid";

export default function ProjectPage({ project, next, prev, press }) {
    if (!project) return <p>Loading...</p>; // Fallback if project data is missing

    const seo = {
        slug: `projects/${project?.slug}`,
        metaTitle:
            project?.seo?.metaTitle ?? "Your One-Stop Destination for Luxury Interiors - Chalk Studio",
        metaDescription:
            project?.seo?.metaDescription ??
            project?.shortDescription ??
            "Browse Chalk Studio’s portfolio of refined residential, commercial, and hospitality interiors shaped by design skill",
    };

    return (
        <>
            <Seo seo={seo} />
            <Banner project={project} />
            <Gallery data={project?.gallery?.data} />
            <Slider1 data={project?.ImageSlider} />
            <Grid data={project?.ImageBlocks?.images?.data} />
            <Blueprints data={project?.Blueprints?.images?.data} />
            <Testimonial data={project?.Testimonial} />
            <AsSeenOn press={press} />
            <Priyanka quote={project?.quoteOnProject} />
            <NextPrev next={next} prev={prev} />
        </>
    );
}

export async function getStaticProps({ params = {} } = {}) {
    let props = {};
    const client = await getUrqlClient();

    try {
        const { data } = await client.query(QUERY_ENDPOINTS.GET_PROJECTS, {
            start: 0,
            limit: 1,
            filter: {
                publishedAt: { ne: null },
                slug: { eq: params?.slug },
            },
        });

        if (!data?.projects?.data?.[0]) {
            return { notFound: true };
        }

        const pressResponse = await client.query(QUERY_ENDPOINTS.QUERY_ALL_PRESS, {
            start: 0,
            limit: 50,
        });

        const next = await client.query(QUERY_ENDPOINTS.GET_PROJECTS, {
            start: 0,
            limit: 1,
            filter: {
                publishedAt: { ne: null },
                id: { gt: data?.projects?.data[0]?.id },
            },
        });

        const prev = await client.query(QUERY_ENDPOINTS.GET_PROJECTS, {
            start: 0,
            limit: 1,
            filter: {
                publishedAt: { ne: null },
                id: { lt: data?.projects?.data[0]?.id },
            },
        });

        props = {
            project: sanitize(data?.projects?.data[0]) ?? {},
            press: pressResponse?.data?.presses?.data?.map((row) => sanitize(row)) ?? [],
            next: sanitize(next?.data?.projects?.data[0]) ?? {},
            prev: sanitize(prev?.data?.projects?.data[0]) ?? {},
        };
    } catch (error) {
        console.log("Error fetching project data:", error);
        return { notFound: true }; // Handle fetch errors gracefully
    }

    return {
        props,
        revalidate: 10,
    };
}

export async function getStaticPaths() {
    let paths = [];
    const client = await getUrqlClient();

    try {
        const { data } = await client.query(QUERY_ENDPOINTS.GET_PROJECTS, {
            start: 0,
            limit: 12,
        });

        const projects = data?.projects?.data.map((row) => sanitize(row)) || [];

        paths = projects
            .filter((project) => project.slug)
            .map((project) => ({
                params: { slug: project.slug },
            }));
    } catch (error) {
        console.log("Error fetching paths:", error);
    }

    return {
        paths,
        fallback: "blocking",
    };
}
